import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, FileText, Loader2, X } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import {
  SensitiveMoney,
  SensitiveText,
} from "@/components/common/Sensitive";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useRightRail } from "@/context/RightRailContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { formatContractRef } from "@/lib/formatContract";
import { SalesPriceStrip } from "@/components/pricing/SalesPriceStrip";
import { salesPriceBreakdown } from "@/lib/salesPrice";
import {
  equipmentLabel,
  salesInputFromBookingLine,
} from "@/lib/salesPriceAdapters";
import { cn } from "@/lib/utils";
import { RateSourceBadge, RateStatusBadge } from "@/features/rates/RateSourceBadge";
import { CollapsibleRailSection } from "@/shell/CollapsibleRailSection";
import {
  railSectionStackClassName,
  railStackInlineScrollBodyClassName,
  railStackInlineScrollRootClassName,
  railStackSectionContentClassName,
  useRailStackPanel,
} from "@/shell/RailStackContext";
import type { ApiBooking, BookingPricingLine } from "@/types/api";
import { isUnmatchedContractRateLine } from "@/lib/bookingCommercial";
import { matchedBookingPrice } from "@/types/booking";

import {
  contractValidityLabel,
  pricingValidityLabel,
  useBookingAssignedPricing,
  useBookingContractCard,
} from "./bookingAssignedRate";

export type BookingAssignedRateRailPayload = {
  sourceId: string;
  booking: Pick<
    ApiBooking,
    | "all_contract_refs"
    | "contract_ref"
    | "all_carriers"
    | "carrier"
    | "named_accounts"
    | "pricing_currency"
  >;
  contractRef?: string | null;
  carrier?: string | null;
  namedAccount?: string | null;
  rateCardId?: string | null;
  pricingTotal?: number | null;
  /**
   * When `true`, the viewer's pricing tier withholds the internal rate-line
   * breakdown (consignee-invoice / hidden) — skip the `/pricing` fetch.
   */
  pricingWithheld?: boolean;
};

interface BookingAssignedRateRailProps extends BookingAssignedRateRailPayload {
  onClose: () => void;
}

function PricingLineBreakdown({ line }: { line: BookingPricingLine }) {
  const breakdown = salesPriceBreakdown(salesInputFromBookingLine(line));
  const unmatchedContract = isUnmatchedContractRateLine(line);

  return (
    <div className="space-y-2 rounded-lg border border-border/60 bg-background/40 p-3 text-sm">
      <div className="flex items-start justify-between gap-2 font-medium">
        <span>
          {equipmentLabel(
            line.equipment_size,
            line.equipment_type,
            line.quantity,
          )}
        </span>
        <SensitiveMoney amount={line.price_total} />
      </div>
      {unmatchedContract && (
        <Badge variant="outline" className="text-[10px] font-normal">
          No contract rate match
        </Badge>
      )}
      {(line.origin_locode || line.destination_locode) && (
        <p className="text-xs text-muted-foreground">
          {[line.origin_locode, line.destination_locode]
            .filter(Boolean)
            .join(" → ")}
        </p>
      )}
      <SalesPriceStrip density="rail" breakdown={breakdown} />
      {line.rate_expired && (
        <Badge variant="destructive" className="text-[10px]">
          Rate expired
        </Badge>
      )}
    </div>
  );
}

function ContractMetaGrid({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg bg-muted/40 px-2.5 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-0.5 text-sm font-medium">{children}</div>
    </div>
  );
}

function AssignedRateCard({
  contractRef,
  carrier,
  namedAccount,
  rateCardId,
  pricingTotal,
  lines,
  loading,
  error,
  onClose,
  showClose,
  stackGrow = false,
}: {
  contractRef?: string | null;
  carrier?: string | null;
  namedAccount?: string | null;
  rateCardId?: string | null;
  pricingTotal?: number | null;
  lines: BookingPricingLine[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  showClose: boolean;
  /** Desktop stack — section body scrolls; no nested scroller. */
  stackGrow?: boolean;
}) {
  const navigate = useNavigate();
  const { formatDate } = useDateFormatter();
  const label = formatContractRef(contractRef ?? "", namedAccount, carrier);
  const {
    card,
    loading: cardLoading,
    error: cardError,
  } = useBookingContractCard(rateCardId, contractRef, carrier);
  const cardValidity = contractValidityLabel(
    card?.valid_from,
    card?.valid_to,
    formatDate,
  );
  const bookingValidity = pricingValidityLabel(lines, formatDate);
  const validity = cardValidity ?? bookingValidity;
  const total = matchedBookingPrice(pricingTotal);
  const openCardId = card?.id ?? rateCardId?.trim();

  const detailsBody = (
    <>
      {cardLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading contract…
        </p>
      )}

      {cardError && !cardLoading && (
        <p className="text-sm text-destructive">{cardError}</p>
      )}

      {card && !cardLoading && (
        <div className="grid grid-cols-2 gap-2">
          <ContractMetaGrid label="Valid from">
            <span className="tabular-nums">{formatDate(card.valid_from)}</span>
          </ContractMetaGrid>
          <ContractMetaGrid label="Valid to">
            <span className="tabular-nums">{formatDate(card.valid_to)}</span>
          </ContractMetaGrid>
          <ContractMetaGrid label="Lines">
            <span className="tabular-nums">
              {card.line_count.toLocaleString()}
            </span>
          </ContractMetaGrid>
          <ContractMetaGrid label="Revision">
            {card.contract_revision ?? "—"}
          </ContractMetaGrid>
        </div>
      )}

      {loading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading booking rate…
        </p>
      )}

      {error && !loading && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!loading && !error && lines.length > 0 && (
        <div
          className={cn(
            "space-y-2",
            !stackGrow && "min-h-0 flex-1 overflow-y-auto pr-1",
            stackGrow && "pr-0",
          )}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Matched on this booking
          </p>
          <div className="space-y-3">
            {lines.map((line) => (
              <PricingLineBreakdown key={line.id} line={line} />
            ))}
          </div>
        </div>
      )}

      {!loading && !error && lines.length === 0 && !cardLoading && (
        <p className="text-sm text-muted-foreground">
          No matched rate lines for this contract on the booking yet.
        </p>
      )}
    </>
  );

  const openCardButton = openCardId ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="relative z-[2] w-full shrink-0"
      onClick={() => {
        navigate(`/rates/${encodeURIComponent(openCardId)}`);
        onClose();
      }}
    >
      Open full rate card
    </Button>
  ) : null;

  return (
    <div
      className={railStackInlineScrollRootClassName(
        stackGrow,
        cn("flex flex-col gap-4", !stackGrow && "h-full min-h-0"),
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {carrier && <CarrierLogo carrier={carrier} size={18} />}
            <span className="font-mono text-sm font-semibold">
              <SensitiveText value={label || contractRef || "Contract"} />
            </span>
            {card && (
              <>
                <RateSourceBadge source={card.source_type} />
                <RateStatusBadge status={card.status} />
              </>
            )}
          </div>
          {card?.named_account && (
            <p className="text-xs text-muted-foreground">
              <SensitiveText value={card.named_account} />
            </p>
          )}
          {validity && (
            <p className="text-xs text-muted-foreground">Valid {validity}</p>
          )}
          {total != null && (
            <p className="text-lg font-semibold tabular-nums">
              <SensitiveMoney amount={total} />
            </p>
          )}
        </div>
        {showClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label="Close contract details"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {stackGrow ? (
        <>
          <div
            className={railStackInlineScrollBodyClassName(
              true,
              "flex flex-col gap-4 pr-0.5",
            )}
          >
            {detailsBody}
          </div>
          {openCardButton}
        </>
      ) : (
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-4",
            lines.length > 0 && "overflow-hidden",
          )}
        >
          {detailsBody}
          {openCardButton}
        </div>
      )}
    </div>
  );
}

/** Desktop stack card for the booking's matched contract rate. */
export function BookingAssignedRateRailSection({
  payload,
  stack = false,
  stackAnimate = false,
  stackGrow = false,
}: {
  payload: BookingAssignedRateRailPayload;
  stack?: boolean;
  stackAnimate?: boolean;
  stackGrow?: boolean;
}) {
  const { toggleBookingAssignedRateSection, setBookingAssignedRateRail } =
    useRightRail();
  const view = useRailStackPanel("assignedRate");
  const {
    sourceId,
    booking,
    contractRef,
    carrier,
    namedAccount,
    rateCardId,
    pricingTotal,
    pricingWithheld,
  } = payload;
  const { lines, loading, error } = useBookingAssignedPricing(
    sourceId,
    booking,
    contractRef,
    pricingWithheld,
  );

  return (
    <CollapsibleRailSection
      title="Contract"
      icon={FileText}
      expanded={view.expanded}
      accordion={view.accordion}
      fill={view.fill}
      flow={view.flow}
      scroll={stackGrow ? false : view.scroll}
      onToggle={toggleBookingAssignedRateSection}
      className={railSectionStackClassName(view, {
        mobileStack: stack,
        stackAnimate,
        stackGrow,
      })}
      contentClassName={stack ? undefined : "pr-0.5"}
    >
      <div className={railStackSectionContentClassName(stackGrow)}>
        <AssignedRateCard
          contractRef={contractRef}
          carrier={carrier}
          namedAccount={namedAccount}
          rateCardId={rateCardId}
          pricingTotal={pricingTotal}
          lines={lines}
          loading={loading}
          error={error}
          onClose={() => setBookingAssignedRateRail(null)}
          showClose
          stackGrow={stackGrow}
        />
      </div>
    </CollapsibleRailSection>
  );
}

/** Mobile bottom sheet for the booking's matched contract rate. */
export function BookingAssignedRateRail({
  sourceId,
  booking,
  contractRef,
  carrier,
  namedAccount,
  rateCardId,
  pricingTotal,
  pricingWithheld,
  onClose,
}: BookingAssignedRateRailProps) {
  const { lines, loading, error } = useBookingAssignedPricing(
    sourceId,
    booking,
    contractRef,
    pricingWithheld,
  );

  const body = (
    <AssignedRateCard
      contractRef={contractRef}
      carrier={carrier}
      namedAccount={namedAccount}
      rateCardId={rateCardId}
      pricingTotal={pricingTotal}
      lines={lines}
      loading={loading}
      error={error}
      onClose={onClose}
      showClose
    />
  );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[min(90dvh,var(--app-vvh,90vh))] max-w-lg rounded-t-2xl pb-safe"
      >
        <SheetHeader className="mb-3 shrink-0 pr-8">
          <SheetTitle>Contract</SheetTitle>
          <SheetDescription>
            Rate card validity, status, and the surcharges matched to this
            booking.
          </SheetDescription>
        </SheetHeader>
        <div className="sheet-scroll flex min-h-0 flex-1 flex-col">{body}</div>
      </SheetContent>
    </Sheet>
  );
}

interface AssignedRateDetailRowProps {
  sourceId: string;
  booking: Pick<
    ApiBooking,
    | "all_contract_refs"
    | "contract_ref"
    | "all_carriers"
    | "carrier"
    | "named_accounts"
    | "pricing_currency"
  >;
  contractRef?: string | null;
  carrier?: string | null;
  namedAccount?: string | null;
  rateCardId?: string | null;
  pricingTotal?: number | null;
  /** Skip the `/pricing` fetch for consignee-invoice / hidden tier viewers. */
  pricingWithheld?: boolean;
  open: boolean;
  onToggle: () => void;
  /** Render only the contract button (for multi-split stacks). */
  embedded?: boolean;
  /** Override the left column label (default `Contract`). */
  rowLabel?: string;
  /** Booking Commercial: show contract + validity only (pricing lives in hero). */
  hidePricingTotal?: boolean;
}

/** Commercial section: contract (+ optional price), opens assigned-rate rail/sheet. */
export function AssignedRateDetailRow({
  sourceId,
  booking,
  contractRef,
  carrier,
  namedAccount,
  rateCardId,
  pricingTotal,
  pricingWithheld,
  open,
  onToggle,
  embedded = false,
  rowLabel = "Contract",
  hidePricingTotal = false,
}: AssignedRateDetailRowProps) {
  const { formatDate } = useDateFormatter();
  const ref = contractRef?.trim();
  const canOpen = Boolean(ref || rateCardId);
  const { lines, loading } = useBookingAssignedPricing(
    canOpen ? sourceId : null,
    canOpen ? booking : null,
    contractRef,
    pricingWithheld,
  );
  const label = formatContractRef(ref ?? "", namedAccount, carrier);
  const validity = lines.some((l) => l.rate_valid_from || l.rate_valid_to)
    ? pricingValidityLabel(lines, formatDate)
    : undefined;
  const total = matchedBookingPrice(pricingTotal);

  if (!ref && (hidePricingTotal || total == null)) {
    if (embedded) {
      return <span className="text-sm font-medium">—</span>;
    }
    return (
      <div className="flex items-start justify-between gap-4 py-2">
        <dt className="text-sm text-muted-foreground">{rowLabel}</dt>
        <dd className="text-right text-sm font-medium">—</dd>
      </div>
    );
  }

  const control = (
    <button
      type="button"
      disabled={!canOpen}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-busy={loading || undefined}
      onClick={canOpen ? onToggle : undefined}
      className={cn(
        "inline-flex max-w-full flex-col items-end gap-0.5 rounded-lg px-1.5 py-0.5 text-right transition-colors",
        canOpen && "hover:bg-accent",
        open && "bg-accent",
        !canOpen && "cursor-default",
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium">
        {carrier ? <CarrierLogo carrier={carrier} size={14} /> : null}
        <span className="font-mono">
          <SensitiveText value={label || ref || "—"} />
        </span>
        {canOpen &&
          (loading ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-90",
              )}
            />
          ))}
      </span>
      {!hidePricingTotal && total != null && (
        <span className="text-sm tabular-nums">
          <SensitiveMoney amount={total} />
        </span>
      )}
      {validity && (
        <span className="text-xs text-muted-foreground">Valid {validity}</span>
      )}
    </button>
  );

  if (embedded) {
    return control;
  }

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-sm text-muted-foreground">{rowLabel}</dt>
      <dd className="min-w-0 text-right">{control}</dd>
    </div>
  );
}
